import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, from, forkJoin, throwError } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { UserManager, UserManagerSettings, User } from 'oidc-client';
import { AuthHTTPService } from './auth-http.service';
import { CryptoExtension } from '../utils/crypto.extension';
import { AppConfig } from './app.config';
import { IAppConfig } from '../models/app-config.model';
import { CookieService } from 'ngx-cookie-service';
import { isEmbedSession } from '../utils/embed-session';
export type UserType = UserModel | undefined;

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private unsubscribe: Subscription[] = [];
    currentUser$: Observable<UserType>;
    currentMenu$: Observable<any>;
    currentAction$: Observable<any>;
    isLoading$: Observable<boolean>;
    currentUserSubject: BehaviorSubject<UserType>;
    currentMenuSubject: BehaviorSubject<any>;
    currentActionSubject: BehaviorSubject<any>;
    isLoadingSubject: BehaviorSubject<boolean>;
    userManager: UserManager;
    appConfig: IAppConfig;
    cookie_username: string;
    cookie_employee_id: string;
    cookie_is_mobile_app: string;
    constructor(
        private _authHttpService: AuthHTTPService,
        private cookieService: CookieService) {
        this.cookie_username = this.cookieService.get('username');
        this.cookie_employee_id = this.cookieService.get('employee_id');
        this.cookie_is_mobile_app = this.cookieService.get('is_mobile_app');
        this.appConfig = AppConfig.getConfig();
        this.isLoadingSubject = new BehaviorSubject<boolean>(false);
        this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
        this.currentMenuSubject = new BehaviorSubject<any>([]);
        this.currentActionSubject = new BehaviorSubject<any>([]);
        this.currentUser$ = this.currentUserSubject.asObservable();
        this.currentMenu$ = this.currentMenuSubject.asObservable();
        this.currentAction$ = this.currentActionSubject.asObservable();
        this.isLoading$ = this.isLoadingSubject.asObservable();
        this.userManager = new UserManager(this.appConfig.oAuthConfig);
        if (!this.cookie_is_mobile_app && !this.cookie_username && !this.cookie_employee_id) {
            this.userManager.events.addAccessTokenExpiring(() => {
                if (isEmbedSession()) {
                    return;
                }
                console.log("Token sắp hết hạn");
                this.userManager.signinSilent().then((user) => {
                    console.log("Token đã được làm mới thành công");
                }).catch((err) => {
                    console.error("Lỗi khi làm mới token:", err);
                    this.redirectToSsoIfAllowed();
                });
            });

            this.userManager.events.addAccessTokenExpired(() => {
                console.log("Token đã hết hạn");
                this.redirectToSsoIfAllowed();
            });

            this.userManager.events.addSilentRenewError((err) => {
                console.error("Lỗi khi làm mới token không hiển thị:", err);
                this.redirectToSsoIfAllowed();
            });
        }
    }

    /** Embed iframe uses token handoff — never kick OIDC (lands on / → dashboard). */
    private redirectToSsoIfAllowed(): void {
        if (isEmbedSession()) {
            console.warn('Embed mode: skip OIDC signinRedirect');
            return;
        }
        void this.userManager.signinRedirect();
    }

    loginLocalSSO(user: string, email: string): Observable<UserType> {
        this.isLoadingSubject.next(true);
        return this._authHttpService.loginSSO(user, email).pipe(
            map((auth: AuthModel) => {
                localStorage.setItem('isSSO', 'true');
                const result = this.setAuthFromLocalStorage(auth);
                return result;
            }),
            switchMap(() => this.getUserByUserName()),
            catchError((err) => {
                console.error('err', err);
                return of(undefined);
            }),
            finalize(() => this.isLoadingSubject.next(false))
        );
    }
    loginLocalMobile(user: string, email: string): Observable<UserType> {
        this.isLoadingSubject.next(true);
        return this._authHttpService.loginMobile(user, email).pipe(
            map((auth: AuthModel) => {
                localStorage.setItem('isSSO', 'true');
                const result = this.setAuthFromLocalStorage(auth);
                return result;
            }),
            switchMap(() => this.getUserByUserName()),
            catchError((err) => {
                console.error('err', err);
                return of(undefined);
            }),
            finalize(() => this.isLoadingSubject.next(false))
        );
    }
    login(username: string, password: string): Observable<UserType> {
        this.isLoadingSubject.next(true);
        return this._authHttpService.login(username, password).pipe(
            map((auth: AuthModel) => {
                localStorage.setItem('isSSO', 'false');
                const result = this.setAuthFromLocalStorage(auth);
                return result;
            }),
            switchMap(() => this.getUserByUserName()),
            catchError(error => {
                console.error('Login error:', error);
                return throwError(error);
            }),
            finalize(() => {
                this.isLoadingSubject.next(false);
            })
        );
    }
    public getUser_SSO(): Promise<User> {
        return this.userManager.getUser();
    }
    public login_SSO(url: string): Promise<void> {
        return this.userManager.signinRedirect({ state: url });
    }
    logout() {
        const isSSO = localStorage.getItem('isSSO') === 'true';
        const embed = isEmbedSession();
        this.removeLocalStorage();

        if (embed) {
            // Stay inside iframe — OIDC signout would leave embed and land on /
            return;
        }

        if (isSSO) {
            this.logout_SSO();
        } else {
            window.location.href = '/auth/sign-in';
        }
    }
    removeLocalStorage() {
        localStorage.clear();
        localStorage.removeItem(this.appConfig.user_key);
        localStorage.removeItem(this.appConfig.auth_key);
        localStorage.removeItem('isSSO');
    }
    public logout_SSO(): Promise<void> {
        return this.userManager.signoutRedirect();
    }
    getUserByUserName() {
        const auth = this.getAuthFromLocalStorage();
        const isSSO = localStorage.getItem('isSSO') === 'true';
        if (!auth || !auth.token) {
            return of(undefined);
        }
        this.isLoadingSubject.next(true);
        return forkJoin({
            dataUser: this._authHttpService.getData(auth.username, auth.token, 'GetDataProfileUser'),
            dataMenu: this._authHttpService.getData(auth.username, auth.token, 'GetDataMenuUser'),
            dataAction: this._authHttpService.getData(auth.username, auth.token, 'GetDataActionUser'),
            dataUserSSO: this.getUser_SSO(),
        }).pipe(
            map((resp: any) => {
                if (
                    !isEmbedSession() &&
                    !this.cookie_is_mobile_app &&
                    !this.cookie_username &&
                    !this.cookie_employee_id &&
                    !auth.username &&
                    !isSSO
                ) {
                    if (resp.dataUserSSO == null) {
                        this.login_SSO(window.location.href);
                    }
                }
                if (resp.dataUser.data.length == 0) {
                    this.logout();
                }
                this.currentUserSubject.next(resp.dataUser.data[0]);
                this.currentMenuSubject.next(resp.dataMenu.data);
                this.currentActionSubject.next(resp.dataAction.data);
                this.setUserFromLocalStorage(resp.dataUser.data[0]);
                return resp.dataUser.data[0];
            }), catchError(error => {
                this.logout();
                return throwError(error);
            }),
            finalize(() => this.isLoadingSubject.next(false)))
    }
    setUserFromLocalStorage(data: any) {
        localStorage.setItem(this.appConfig.user_key, CryptoExtension.encryptTable(JSON.stringify(data)));
    }
    private getAuthFromLocalStorage(): AuthModel | undefined {
        try {
            const lsValue = localStorage.getItem(this.appConfig.auth_key);
            if (!lsValue) {
                return undefined;
            }
            const authData = JSON.parse(CryptoExtension.decryptStr(lsValue));
            return authData;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }
    private setAuthFromLocalStorage(auth: AuthModel): boolean {
        // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
        if (auth && auth.token) {
            localStorage.setItem(this.appConfig.auth_key, CryptoExtension.encryptTable(JSON.stringify(auth)));
            return true;
        }
        return false;
    }
    get currentUserValue(): UserType {
        return this.currentUserSubject.value;
    }

    set currentUserValue(user: UserType) {
        this.currentUserSubject.next(user);
    }
    get currentMenuValue(): any {
        return this.currentMenuSubject.value;
    }

    set currentMenuValue(data) {
        this.currentMenuSubject.next(data);
    }
    get currentActionValue(): any {
        return this.currentActionSubject.value;
    }

    set currentActionValue(data) {
        this.currentActionSubject.next(data);
    }
    checkActionWidthID(dataID) {
        let dataAction = this.currentActionValue;
        let dataFilter = dataAction.filter(x => x.ID == dataID);
        if (dataFilter.length > 0) {
            return true;
        }
        return false;
    }
    checkMenuWidthID(dataID) {
        let dataMenu = this.currentMenuValue;
        let dataFilter = dataMenu.filter(x => x.ID == dataID);
        if (dataFilter.length > 0) {
            return true;
        }
        return false;
    }
    getAuthToken() {
        const auth = this.getAuthFromLocalStorage();
        if (!auth || !auth.username) {
            return "";
        }
        return auth.token;
    }
    getUsername() {
        const auth = this.getAuthFromLocalStorage();
        if (!auth || !auth.username) {
            return "";
        }
        return auth.username;
    }
    getUserFromLocalStorage(): any {
        try {
            const lsValue = localStorage.getItem(this.appConfig.user_key);
            if (!lsValue) {
                return undefined;
            }

            const data = JSON.parse(CryptoExtension.decryptStr(lsValue));
            return data;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }
    ngOnDestroy() {
        this.unsubscribe.forEach((sb) => sb.unsubscribe());
    }
}
